import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useFeedback } from '../../hooks/useFeedback';

export interface NPSModalProps {
  isOpen: boolean;
  onClose: (action: 'submitted' | 'later' | 'dismissed') => void;
}

const NPS_MAX_REASON = 500;

export function NPSModal({ isOpen, onClose }: NPSModalProps) {
  const [score, setScore] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const { submit, isSubmitting, error } = useFeedback();
  const closedRef = useRef(false);

  // Escape key → dismissed
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose('dismissed');
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setScore(null);
      setReason('');
      closedRef.current = false;
    }
  }, [isOpen]);

  const handleSubmitSafe = async () => {
    if (score === null || isSubmitting || closedRef.current) return;
    try {
      await submit({
        feedbackType: 'nps',
        nps: {
          score,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
      });
      closedRef.current = true;
      onClose('submitted');
    } catch {
      // error rendered inline by useFeedback; do not close
    }
  };

  const handleLater = () => {
    onClose('later');
  };

  const handleNoThanks = () => {
    onClose('dismissed');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
          // Backdrop click intentionally NOT closing — NPS needs deliberate response
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ type: 'spring', damping: 30, stiffness: 400 }}
            className="w-full max-w-lg bg-white dark:bg-[hsl(220,15%,12%)] rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 overflow-hidden"
          >
            {/* Header */}
            <div className="p-5 border-b border-gray-100 dark:border-white/5">
              <h2 className="text-meta font-semibold text-gray-900 dark:text-white">
                How likely are you to recommend OctoPM to a colleague?
              </h2>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              {/* Score chips */}
              <div>
                <div className="flex flex-wrap gap-1.5">
                  {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                    <button
                      key={n}
                      onClick={() => setScore(n)}
                      disabled={isSubmitting}
                      className={[
                        'w-9 h-9 rounded-md text-meta font-medium transition-colors disabled:opacity-50 flex-shrink-0',
                        score === n
                          ? 'bg-primary text-white border border-primary'
                          : 'border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:border-primary/50 hover:bg-primary/5',
                      ].join(' ')}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-micro text-gray-400 dark:text-gray-500">Not likely</span>
                  <span className="text-micro text-gray-400 dark:text-gray-500">Extremely likely</span>
                </div>
              </div>

              {/* Reason textarea — only shown after score selected */}
              {score !== null && (
                <div>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value.slice(0, NPS_MAX_REASON))}
                    placeholder="What's the main reason for your score?"
                    rows={3}
                    disabled={isSubmitting}
                    maxLength={NPS_MAX_REASON}
                    className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-meta text-gray-800 dark:text-gray-200 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 placeholder:text-gray-500 dark:placeholder:text-gray-400 resize-y disabled:opacity-60"
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-micro text-gray-400 dark:text-gray-500">
                      {reason.length}/{NPS_MAX_REASON}
                    </span>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <p className="text-micro text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-2.5">
                  {error}
                </p>
              )}

              {/* Actions — only shown after score selected */}
              {score !== null && (
                <div className="flex flex-col gap-2 pt-1">
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => void handleSubmitSafe()}
                    disabled={isSubmitting}
                    className="flex items-center justify-center gap-1.5 w-full py-2.5 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      'Submit'
                    )}
                  </motion.button>
                  <button
                    onClick={handleLater}
                    disabled={isSubmitting}
                    className="w-full py-2 text-meta text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
                  >
                    Remind me in a week
                  </button>
                  <button
                    onClick={handleNoThanks}
                    disabled={isSubmitting}
                    className="w-full py-1.5 text-micro text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
                  >
                    No thanks
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
